import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Box, Button, Chip, Divider, Grid, IconButton, Input, InputAdornment, Stack, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import { AccountBalanceWalletOutlined, FileDownloadOutlined } from "@mui/icons-material";
import moment from "moment";
import Loader from "../../../UiComponents/Loader/Loader";
import MasterCommonTable from "../../../UiComponents/CommonTable/MasterCommonTable";
import FilterBox from "../../../UiComponents/FilterBox/FilterBox";
import { ToastError, ToastSuccess } from "../../../UiComponents/Toaster/Toast";
import { checkBriskV2Topup, getBriskV2Config, getBriskV2WalletSummary, getBriskV2WalletTopups } from "../../../Service/BriskV2Service";
import { BriskTopupDialog } from "../BriskCertificateList/BriskV2Dialogs";
import { isSafeLink } from "../BriskCertificateList/briskV2ListUtils";
import { MismatchAlert, agencyOptions, openReceipt } from "./BriskWalletParts";
import {
  HISTORY_EXCEL_HEADERS,
  HISTORY_STATUS_FILTERS,
  dayParam,
  historyExcelRows,
  rupees,
  saveExcel,
  statusColorOf,
  topupStatusText,
  whenText,
} from "./briskWalletUtils";

/**
 * Transaction History (owner, 2026-10-30, after Girnar's "Wallet Transaction History") — how money was put into the
 * agency's Company Wallet "Brisk": Brisk online top-ups (Cashfree, added only after the payment is confirmed, once) and
 * the admin's own Top-up / Remove. There is no button that marks a top-up paid by hand. Admin only; the super admin
 * sees every agency (Agency filter) but pays and checks nothing.
 */
const BriskWalletHistory = () => {
  const [config, setConfig] = useState(null);
  const [agencies, setAgencies] = useState([]);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [wallet, setWallet] = useState(null);
  const [problem, setProblem] = useState("");
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [status, setStatus] = useState("");
  const [agency, setAgency] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [applied, setApplied] = useState({ status: "", clientId: "", startDate: null, endDate: null });
  const [showFilter, setShowFilter] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [topupOpen, setTopupOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const superAdmin = !!(config && config.isSuperAdmin);
  const canTopup = !!(config && config.can && config.can.topup);

  useEffect(() => {
    getBriskV2Config().then((res) => {
      if (res.error) ToastError(res.message);
      else setConfig(res.data);
    });
  }, []);

  useEffect(() => {
    if (!superAdmin) return;
    getBriskV2WalletSummary().then((res) => {
      if (!res.error && res.data && res.data.superAdmin) setAgencies(res.data.rows || []);
    });
  }, [superAdmin]);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(search), 500);
    return () => clearTimeout(handler);
  }, [search]);

  const query = useMemo(
    () => ({
      status: applied.status,
      clientId: applied.clientId,
      startDate: dayParam(applied.startDate),
      endDate: dayParam(applied.endDate),
      search: debouncedSearch,
    }),
    [applied, debouncedSearch]
  );
  const queryKey = JSON.stringify(query);

  useEffect(() => setPage(0), [queryKey]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getBriskV2WalletTopups({ ...query, page: page + 1, limit }).then((res) => {
      if (!alive) return;
      setLoading(false);
      if (res.error) {
        setProblem(res.message || "The Brisk wallet could not be read");
        setRows([]);
        setTotal(0);
        return;
      }
      setProblem("");
      setWallet(res.data.wallet || null);
      setRows(
        (res.data.rows || []).map((r) => ({
          ...r,
          dateText: whenText(r.at),
          userText: r.userName || "-",
          typeText: r.source === "ONLINE" ? "Brisk online" : "Admin",
          orderText: r.orderId || "-",
          transactionText: r.transactionId || "-",
          statusText: topupStatusText(r.status),
          amountText: rupees(r.amount),
        }))
      );
      setTotal(res.data.total || 0);
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryKey, page, limit, refreshKey]);

  const refresh = () => setRefreshKey((k) => k + 1);

  const check = async (row) => {
    setLoading(true);
    const res = await checkBriskV2Topup(row.id);
    setLoading(false);
    if (res.error) ToastError(res.message);
    else ToastSuccess(res.message || "Checked");
    refresh();
  };

  const pay = (row) => {
    if (isSafeLink(row.checkoutUrl)) window.open(row.checkoutUrl, "_blank", "noopener,noreferrer");
  };

  const toDate = (d) => (d && d.toDate ? d.toDate() : d);
  const apply = (patch = {}) => setApplied({ status, clientId: agency, startDate: toDate(startDate), endDate: toDate(endDate), ...patch });
  const agencyList = agencyOptions(agencies);

  const filterData = [
    {
      id: 1,
      label: "Status",
      data: HISTORY_STATUS_FILTERS,
      setState: (e) => {
        const option = HISTORY_STATUS_FILTERS.find((el) => el._id === e);
        if (option) setStatus(option.value);
      },
      value: (HISTORY_STATUS_FILTERS.find((opt) => opt.value === status) || HISTORY_STATUS_FILTERS[0])._id,
      type: "input",
      variation: "single",
    },
    {
      id: 2,
      type: "noDefaultDate",
      label: "Transaction Date",
      defaultValue: [startDate, endDate],
      setDate: (dates) => {
        if (Array.isArray(dates) && dates.length >= 2 && dates[0] && dates[1]) {
          setStartDate(dates[0]);
          setEndDate(dates[1]);
          apply({ startDate: toDate(dates[0]), endDate: toDate(dates[1]) });
        } else {
          setStartDate(null);
          setEndDate(null);
          apply({ startDate: null, endDate: null });
        }
      },
    },
    ...(superAdmin
      ? [
          {
            id: 3,
            label: "Agency",
            data: agencyList,
            setState: (e) => {
              const option = agencyList.find((el) => el._id === e);
              if (option) setAgency(option.value);
            },
            value: (agencyList.find((opt) => opt.value === agency) || agencyList[0])._id,
            type: "input",
            variation: "single",
          },
        ]
      : []),
  ];

  const download = async () => {
    setExporting(true);
    const res = await getBriskV2WalletTopups({ ...query, export: "1" });
    setExporting(false);
    if (res.error) {
      ToastError(res.message);
      return;
    }
    const headers = superAdmin ? ["Agency", ...HISTORY_EXCEL_HEADERS] : HISTORY_EXCEL_HEADERS;
    await saveExcel(`Brisk Transaction History ${moment().format("DD-MM-YYYY")}.xlsx`, headers, historyExcelRows(res.data.rows, { withAgency: superAdmin }));
    if (res.data.truncated) ToastError(`Only the newest ${res.data.rows.length} rows were downloaded. Please choose dates for the rest`);
  };

  const smallButton = { textTransform: "none", borderRadius: "6px", fontSize: 12, minWidth: 0, px: 1.25, py: 0.25, whiteSpace: "nowrap" };
  const columns = [
    { id: "dateText", label: "Transaction Date", placeMent: true, disablePadding: false },
    ...(superAdmin ? [{ id: "agencyName", label: "Agency", placeMent: true, disablePadding: false }] : []),
    { id: "userText", label: "User", placeMent: true, disablePadding: false },
    { id: "typeText", label: "Type", placeMent: true, disablePadding: false },
    { id: "orderText", label: "Order ID", placeMent: true, disablePadding: false },
    { id: "transactionText", label: "Transaction ID", placeMent: true, disablePadding: false },
    { id: "statusText", label: "Status", placeMent: true, disablePadding: false, type: "coloredText", getColor: (row) => statusColorOf(row.status) },
    {
      id: "amountCell",
      label: "Amount",
      placeMent: false,
      disablePadding: false,
      type: "component",
      renderComponent: (row) => (
        <Box>
          <Typography sx={{ fontSize: 13, color: row.status === "REMOVED" ? "#B42318" : "#101828" }}>
            {row.status === "REMOVED" ? `- ${row.amountText}` : row.amountText}
          </Typography>
          {row.credited && row.credited !== row.amount && (
            <Typography sx={{ fontSize: 12, color: "#667085" }}>{rupees(row.credited)} added</Typography>
          )}
        </Box>
      ),
    },
    {
      id: "apiEnv",
      label: "Brisk",
      placeMent: false,
      disablePadding: false,
      type: "component",
      renderComponent: (row) =>
        row.apiEnv ? <Chip size="small" label={row.apiEnv} color={row.apiEnv === "TEST" ? "warning" : "success"} /> : <span>-</span>,
    },
    {
      id: "actions",
      label: "Action",
      placeMent: false,
      disablePadding: false,
      type: "component",
      renderComponent: (row) => (
        <Stack direction="row" spacing={0.75} sx={{ flexWrap: "nowrap" }}>
          {row.checkoutUrl && (
            <Button size="small" variant="outlined" onClick={() => pay(row)} sx={smallButton}>
              Pay
            </Button>
          )}
          {row.canCheck && (
            <Button size="small" variant="outlined" onClick={() => check(row)} sx={smallButton}>
              Check status
            </Button>
          )}
          {row.receiptKey && (
            <Button size="small" variant="outlined" onClick={() => openReceipt(row.receiptKey)} sx={smallButton}>
              View receipt
            </Button>
          )}
        </Stack>
      ),
    },
  ];

  const activeFiltersCount = (status ? 1 : 0) + (startDate && endDate ? 1 : 0) + (agency ? 1 : 0);

  return (
    <div style={{ margin: "1px" }}>
      {loading && <Loader />}
      <Grid container className="Master_Card_Container">
        <Grid container alignItems={"center"} justifyContent={"space-between"} className="Master_Card_Sub_Container" sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Typography className="Master_Card_Tittle">
              Transaction History
              {config && config.apiEnv === "TEST" && <Chip size="small" color="warning" label="TEST" sx={{ ml: 1, verticalAlign: "middle" }} />}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={12} md={8} className="d-flex" justifyContent={{ xs: "start", sm: "flex-end", md: "flex-end" }}>
            <Stack direction={{ xs: "column", sm: "row", md: "row" }} spacing={2} mt={{ xs: 2, sm: 2, md: 0 }}>
              {!superAdmin && wallet && (
                <Button className="Master_Card_Filter_Button" sx={{ height: "40px", gap: "8px", px: 2, whiteSpace: "nowrap", cursor: "default" }} disableRipple>
                  Wallet Balance = {rupees(wallet.balance)}
                </Button>
              )}
              {canTopup && (
                <Button
                  className="Master_Card_Filter_Button"
                  sx={{ height: "40px", gap: "8px", px: 2, whiteSpace: "nowrap" }}
                  startIcon={<AccountBalanceWalletOutlined />}
                  onClick={() => setTopupOpen(true)}
                >
                  Top up Brisk
                </Button>
              )}
              <Button
                className="Master_Card_Filter_Button"
                sx={{ height: "40px", gap: "8px", px: 2, whiteSpace: "nowrap" }}
                startIcon={<FileDownloadOutlined />}
                disabled={exporting}
                onClick={download}
              >
                Download Excel
              </Button>
              <Button
                className="Master_Card_Filter_Button w-100"
                sx={{ width: "102px", height: "40px", gap: "8px" }}
                startIcon={
                  <Badge badgeContent={activeFiltersCount} color="error">
                    <FilterListIcon sx={{ marginRight: "1.5px" }} />
                  </Badge>
                }
                onClick={() => setShowFilter(!showFilter)}
              >
                Filters
              </Button>
            </Stack>
          </Grid>
        </Grid>

        {showFilter && (
          <Box sx={{ mb: 2, overflow: "visible", position: "relative", zIndex: 100, minHeight: "60px", width: "100%" }}>
            <FilterBox
              filterData={filterData}
              type={"single"}
              handleSaveAllFilter={() => apply()}
              handleClearAllState={() => {
                setStatus("");
                setAgency("");
                setStartDate(null);
                setEndDate(null);
                setApplied({ status: "", clientId: "", startDate: null, endDate: null });
              }}
              handleClearSingleFilter={(filterId) => {
                if (filterId === 1) setStatus("");
                if (filterId === 2) {
                  setStartDate(null);
                  setEndDate(null);
                }
                if (filterId === 3) setAgency("");
              }}
              horizontalLayout={true}
              hideHeader={true}
              hideInput={true}
              alwaysOpen={true}
              autoApply={true}
            />
          </Box>
        )}

        <Grid item xs={12}>
          <Divider sx={{ backgroundColor: "#EAECF0", borderWidth: 1, mb: 2 }} />
        </Grid>

        <MismatchAlert wallet={wallet} />
        {problem && (
          <Alert severity="error" sx={{ mb: 2, width: "100%" }}>
            {problem}
          </Alert>
        )}

        <Grid container alignItems={"center"} justifyContent={"flex-end"} className="Master_Card_Sub_Container" sx={{ mb: 2 }}>
          <Grid item xs={12} sm={4} md={3.1}>
            <Input
              className="w-100 Master_Card_Input"
              type="text"
              placeholder="Search order, transaction ID, remarks"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              disableUnderline={true}
              startAdornment={
                <InputAdornment position="start" sx={{ marginRight: "0px" }}>
                  <IconButton onClick={() => setDebouncedSearch(search)} aria-label="search">
                    <SearchIcon sx={{ fontSize: "20px" }} />
                  </IconButton>
                </InputAdornment>
              }
            />
          </Grid>
        </Grid>

        <MasterCommonTable
          data={rows}
          tableTotalCount={total}
          columns={columns}
          isServerSide={true}
          limit={limit}
          paginationReset={queryKey}
          rowsPerPageOptions={[10, 25, 50, 100]}
          tableOutputEvent={(event) => {
            if (event.page !== undefined) setPage(event.page - 1);
            if (event.limit) setLimit(event.limit);
          }}
          tableConfig={{ isAction: false, isSearch: false, isView: false, isEdit: false, isDelete: false, isMultiDelete: false, isDownload: false }}
        />
      </Grid>
      <BriskTopupDialog open={topupOpen} onClose={() => setTopupOpen(false)} onCredited={refresh} />
    </div>
  );
};

export default BriskWalletHistory;
